// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { useEffect, useRef, useCallback } from 'react';

interface UseAnnounceReturn {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

export function useAnnounce(): UseAnnounceReturn {
  const politeRef = useRef<HTMLDivElement | null>(null);
  const assertiveRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const createRegion = (priority: 'polite' | 'assertive'): HTMLDivElement => {
      const div = document.createElement('div');
      div.setAttribute('aria-live', priority);
      div.setAttribute('aria-atomic', 'true');
      /*
       * NO `role`. `aria-live` alone announces; `role="alert"` additionally
       * declares the element to BE an alert, and these two regions are mounted
       * empty for the lifetime of every component that calls this hook.
       *
       * Sprint 64.40.0 made `ExportFormatMenu` an announcer, and that component
       * is on most admin pages — so an empty, permanently-present
       * `role="alert"` appeared across the product and shadowed each page's
       * real error banner. A screen-reader user reaches an alert with nothing
       * in it before reaching the message that matters, and
       * `findByRole('alert')` resolves to the phantom: three AskForsetiModal
       * tests failed with "expected 'Export failed: …', received ''" while the
       * page's own alert was rendered correctly two nodes away.
       *
       * `role="alert"` implies `aria-live="assertive"` and `role="status"`
       * implies `polite`, so the attribute was redundant for announcing and
       * load-bearing only for the shadowing. Dropped.
       */
      div.style.position = 'absolute';
      div.style.width = '1px';
      div.style.height = '1px';
      div.style.padding = '0';
      div.style.margin = '-1px';
      div.style.overflow = 'hidden';
      div.style.clip = 'rect(0, 0, 0, 0)';
      div.style.whiteSpace = 'nowrap';
      div.style.border = '0';
      document.body.appendChild(div);
      return div;
    };

    politeRef.current = createRegion('polite');
    assertiveRef.current = createRegion('assertive');

    return () => {
      if (politeRef.current) {
        document.body.removeChild(politeRef.current);
        politeRef.current = null;
      }
      if (assertiveRef.current) {
        document.body.removeChild(assertiveRef.current);
        assertiveRef.current = null;
      }
    };
  }, []);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const region = priority === 'assertive' ? assertiveRef.current : politeRef.current;
    if (region) {
      // Clear first to ensure re-announcement of identical messages
      region.textContent = '';
      requestAnimationFrame(() => {
        region.textContent = message;
      });
    }
  }, []);

  return { announce };
}
